import React from 'react';
import {
  axisClasses,
  barElementClasses,
  BarPlot,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  ResponsiveChartContainer
} from "@mui/x-charts";
import {
  DashboardAtrasoPorSistema,
  DashboardAtrasoPorSistemaFields,
  DashboardPromedioResolucion,
  DashboardPromedioResolucionFields
} from "../types/dashboard";
import {useTheme} from "@mui/material";
import CardActions from "../../sistema/components/CardActions";
import {hexToRGBA} from "../../../@core/utils/hex-to-rgba";

interface AtrasoResolucionPorSistemaChartProps {
  atrasoData: DashboardAtrasoPorSistema[];
  resolucionData: DashboardPromedioResolucion[];
}

interface ChartRow {
  [key: string]: string | number;
  sistema: string;
  abreviacion: string;
  atrasoPromedioDias: number;
  promedioResolucionDias: number;
}

const getPromediosResolucion = (data: DashboardPromedioResolucion[]) => {
  return data.reduce<Record<string, { total: number; cantidadMensajes: number; cantidadEtapas: number; abreviacion: string }>>((acc, item) => {
    const sistema = item[DashboardPromedioResolucionFields.Sistema];
    const cantidad = item[DashboardPromedioResolucionFields.CantMensajes] || 0;
    const promedio = item[DashboardPromedioResolucionFields.AtrasoPromedioDias] || 0;

    if (!acc[sistema]) {
      acc[sistema] = {
        total: 0,
        cantidadMensajes: 0,
        cantidadEtapas: 0,
        abreviacion: item[DashboardPromedioResolucionFields.Abreviacion],
      };
    }

    acc[sistema].total += promedio * (cantidad || 1);
    acc[sistema].cantidadMensajes += cantidad;
    acc[sistema].cantidadEtapas += 1;

    return acc;
  }, {});
}

export default function AtrasoResolucionPorSistemaChart({
  atrasoData,
  resolucionData
}: AtrasoResolucionPorSistemaChartProps) {
  const theme = useTheme();
  const promediosResolucion = getPromediosResolucion(resolucionData);
  const atrasoPorSistema = atrasoData.reduce<Record<string, DashboardAtrasoPorSistema>>((acc, item) => {
    acc[item[DashboardAtrasoPorSistemaFields.Sistema]] = item;

    return acc;
  }, {});
  const sistemas = Array.from(new Set([
    ...atrasoData.map(item => item[DashboardAtrasoPorSistemaFields.Sistema]),
    ...resolucionData.map(item => item[DashboardPromedioResolucionFields.Sistema]),
  ]));

  const dataset: ChartRow[] = sistemas.map(sistema => {
    const atraso = atrasoPorSistema[sistema];
    const resolucion = promediosResolucion[sistema];

    return {
      sistema,
      abreviacion: atraso?.[DashboardAtrasoPorSistemaFields.Abreviacion] || resolucion?.abreviacion || sistema,
      atrasoPromedioDias: atraso?.[DashboardAtrasoPorSistemaFields.AtrasoPromedioDias] || 0,
      promedioResolucionDias: resolucion && resolucion.cantidadEtapas > 0
        ? resolucion.total / (resolucion.cantidadMensajes || resolucion.cantidadEtapas)
        : 0,
    };
  });

  return (
    <CardActions title={'Atraso vs Promedio Resolucion'} colapsable>
      <ResponsiveChartContainer
        height={400}
        dataset={dataset}
        series={[
          {
            type: 'bar',
            dataKey: 'atrasoPromedioDias',
            label: 'Atraso promedio (dias)',
            id: 'atrasoPromedioDias',
            yAxisId: 'dias',
            color: hexToRGBA(theme.palette.warning.main, 0.75),
          },
          {
            type: 'bar',
            dataKey: 'promedioResolucionDias',
            label: 'Promedio resolucion (dias)',
            id: 'promedioResolucionDias',
            yAxisId: 'dias',
            color: hexToRGBA(theme.palette.primary.main, 0.75),
          },
        ]}
        xAxis={[
          {
            dataKey: 'abreviacion',
            valueFormatter: (abreviacion, context) =>
              context.location === 'tick'
                ? abreviacion
                : `Workflow: ${dataset.find((d) => d.abreviacion === abreviacion)?.sistema} (${abreviacion})`,
            scaleType: 'band',
            id: 'x-axis-id',
          },
        ]}
        margin={{left: 70, right: 30}}
        sx={{
          [`.${axisClasses.left} .${axisClasses.label}`]: {
            transform: 'translate(-25px, 0)',
          },
          [`.${barElementClasses.root}`]: {
            strokeWidth: 1,
            clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 1px), 0 calc(100% - 1px))'
          },
        }}
        yAxis={[
          {
            id: 'dias',
          },
        ]}
      >
        <BarPlot/>
        <ChartsTooltip/>
        <ChartsYAxis axisId="dias" label="Dias"/>
        <ChartsXAxis label="Sistemas" position="bottom" axisId="x-axis-id"/>
      </ResponsiveChartContainer>
    </CardActions>
  );
}
